import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useTenant } from "@/pages/shop/tenantContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2,
  Stethoscope,
  TestTube2,
  Copy,
  Share2,
  CheckCircle2,
  Home as HomeIcon,
  Building2,
  Calendar,
  Clock,
  XCircle,
  ShoppingBag,
} from "lucide-react";

const IST_SLOT = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return iso;
  }
};

const todayStr = () => new Date().toISOString().slice(0, 10);

function ConsentLine({ checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} className="mt-0.5" />
      <span>
        I consent to my health information being collected and used to provide this service.
      </span>
    </label>
  );
}

function SaveLinkBanner({ url, primary, message }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — long-press the link instead");
    }
  };
  const share = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`${message}\n\n${url}`)}`,
      "_blank",
      "noopener",
    );
  };
  return (
    <Card className="border-2" style={{ borderColor: primary }} data-testid="care-save-banner">
      <CardContent className="p-4 space-y-3" style={{ background: `${primary}0F` }}>
        <p className="text-sm text-slate-700 leading-snug">
          <b>Save this link now</b> — it's the only way to check your report later. No login needed.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={copy} className="flex-1 gap-2" style={{ background: primary, color: "#fff" }}>
            <Copy className="w-4 h-4" /> Copy link
          </Button>
          <Button
            variant="outline"
            onClick={share}
            className="flex-1 gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Share2 className="w-4 h-4" /> Send to WhatsApp
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DoctorBooking({ ownerId, primary }) {
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [doctorId, setDoctorId] = useState("");
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotStart, setSlotStart] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [existingName, setExistingName] = useState(null);
  const [relationNote, setRelationNote] = useState("");

  const nameConflict =
    existingName && name.trim() && existingName.trim().toLowerCase() !== name.trim().toLowerCase();

  const checkPhone = async () => {
    if (!phone.trim()) return;
    try {
      const { data } = await api.get("/care/patient-lookup", { params: { owner_id: ownerId, phone } });
      setExistingName(data?.exists ? data.name : null);
    } catch {
      setExistingName(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api
      .get("/care/doctors", { params: { owner_id: ownerId } })
      .then(({ data }) => {
        if (cancelled) return;
        setDoctors(data || []);
        if (data?.[0]) setDoctorId(data[0].id);
      })
      .catch(() => toast.error("Could not load doctors"))
      .finally(() => !cancelled && setLoadingDoctors(false));
    return () => {
      cancelled = true;
    };
  }, [ownerId]);

  const loadSlots = useCallback(() => {
    if (!doctorId || !date) return;
    setLoadingSlots(true);
    setSlotStart("");
    api
      .get("/care/doctor-availability", { params: { owner_id: ownerId, doctor_id: doctorId, date } })
      .then(({ data }) => setSlots(data || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [ownerId, doctorId, date]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  const submit = async () => {
    if (!slotStart) return toast.error("Pick a time slot");
    if (!name.trim() || !phone.trim()) return toast.error("Enter your name and phone number");
    if (nameConflict && !relationNote.trim()) {
      return toast.error(`Please note ${name.trim()}'s relationship to ${existingName}`);
    }
    if (!consent) return toast.error("Please confirm consent to continue");
    setBooking(true);
    try {
      const { data } = await api.post("/care/appointments", {
        owner_id: ownerId,
        doctor_id: doctorId,
        slot_start: slotStart,
        patient_name: name,
        patient_phone: phone,
        consent_given: true,
        relation_note: nameConflict ? relationNote.trim() : null,
      });
      setConfirmed(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book this appointment");
      loadSlots();
    } finally {
      setBooking(false);
    }
  };

  if (confirmed) {
    const doctor = doctors.find((d) => d.id === confirmed.doctor_id);
    return (
      <Card className="border-emerald-200" data-testid="appointment-confirmed">
        <CardContent className="p-6 text-center space-y-3">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
          <div className="text-lg font-medium">Appointment booked</div>
          <p className="text-sm text-slate-600">
            {doctor?.name || "Doctor"} · {new Date(confirmed.slot_start).toLocaleDateString("en-IN")} ·{" "}
            {IST_SLOT(confirmed.slot_start)}
          </p>
          {confirmed.relation_note && (
            <p className="text-xs text-slate-400">
              {confirmed.patient_name} ({confirmed.relation_note})
            </p>
          )}
          <p className="text-xs text-slate-400">We'll see you at the clinic at your scheduled time.</p>
        </CardContent>
      </Card>
    );
  }

  if (loadingDoctors) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (doctors.length === 0) {
    return (
      <p className="text-sm text-slate-500 text-center py-8">
        No doctors are set up for booking yet — please check back later.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Doctor</Label>
        <div className="grid gap-2 sm:grid-cols-2">
          {doctors.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setDoctorId(d.id)}
              className={`text-left rounded-lg border p-3 transition ${
                doctorId === d.id ? "border-2" : "border-slate-200 hover:border-slate-300"
              }`}
              style={doctorId === d.id ? { borderColor: primary, background: `${primary}0C` } : undefined}
              data-testid={`care-doctor-${d.id}`}
            >
              <div className="text-sm font-medium">{d.name}</div>
              <div className="text-xs text-slate-500">{d.specialization}</div>
              {d.consult_fee > 0 && (
                <div className="mono text-xs text-slate-400 mt-1">₹{Number(d.consult_fee).toLocaleString("en-IN")} consult</div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="care-date">Date</Label>
        <Input
          id="care-date"
          type="date"
          min={todayStr()}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="care-date-input"
        />
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" /> Available times
        </Label>
        {loadingSlots ? (
          <div className="text-sm text-slate-400 py-4">Checking availability…</div>
        ) : slots.length === 0 ? (
          <div className="text-sm text-slate-400 py-4">No open slots this day — try another date.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {slots.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSlotStart(s)}
                className={`mono text-xs px-3 py-1.5 rounded-md border ${
                  slotStart === s ? "text-white border-transparent" : "border-slate-200 hover:border-slate-300"
                }`}
                style={slotStart === s ? { background: primary } : undefined}
                data-testid={`care-slot-${s}`}
              >
                {IST_SLOT(s)}
              </button>
            ))}
          </div>
        )}
      </div>

      {slotStart && (
        <div className="space-y-3 pt-2 border-t border-slate-100">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="care-name">Your name</Label>
              <Input id="care-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="care-patient-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="care-phone">Phone number</Label>
              <Input
                id="care-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onBlur={checkPhone}
                data-testid="care-patient-phone"
              />
            </div>
          </div>
          {nameConflict && (
            <div className="space-y-2">
              <Label htmlFor="care-relation">
                This phone is on file for <b>{existingName}</b> — how is {name.trim() || "this patient"} related?
              </Label>
              <Input
                id="care-relation"
                placeholder={`e.g. wife of ${existingName}`}
                value={relationNote}
                onChange={(e) => setRelationNote(e.target.value)}
                data-testid="care-appt-relation"
              />
            </div>
          )}
          <ConsentLine checked={consent} onChange={setConsent} testId="care-appt-consent" />
          <Button
            onClick={submit}
            disabled={booking}
            className="w-full"
            style={{ background: primary }}
            data-testid="care-appt-submit"
          >
            {booking ? "Booking…" : "Confirm appointment"}
          </Button>
        </div>
      )}
    </div>
  );
}

function PathologyBooking({ ownerId, primary }) {
  const [tests, setTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [mode, setMode] = useState("walk_in");
  const [address, setAddress] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [existingName, setExistingName] = useState(null);
  const [relationNote, setRelationNote] = useState("");

  const nameConflict =
    existingName && name.trim() && existingName.trim().toLowerCase() !== name.trim().toLowerCase();

  const checkPhone = async () => {
    if (!phone.trim()) return;
    try {
      const { data } = await api.get("/care/patient-lookup", { params: { owner_id: ownerId, phone } });
      setExistingName(data?.exists ? data.name : null);
    } catch {
      setExistingName(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api
      .get("/care/pathology-catalog", { params: { owner_id: ownerId } })
      .then(({ data }) => !cancelled && setTests(data || []))
      .catch(() => toast.error("Could not load the test list"))
      .finally(() => !cancelled && setLoadingTests(false));
    return () => {
      cancelled = true;
    };
  }, [ownerId]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const total = tests.filter((t) => selected.has(t.id)).reduce((s, t) => s + Number(t.price || 0), 0);

  const submit = async () => {
    if (selected.size === 0) return toast.error("Select at least one test");
    if (!name.trim() || !phone.trim()) return toast.error("Enter your name and phone number");
    if (mode === "home" && !address.trim()) return toast.error("Enter a collection address");
    if (nameConflict && !relationNote.trim()) {
      return toast.error(`Please note ${name.trim()}'s relationship to ${existingName}`);
    }
    if (!consent) return toast.error("Please confirm consent to continue");
    setBooking(true);
    try {
      const { data } = await api.post("/care/pathology-orders", {
        owner_id: ownerId,
        test_ids: Array.from(selected),
        patient_name: name,
        patient_phone: phone,
        collection_mode: mode,
        address: mode === "home" ? address : null,
        consent_given: true,
        relation_note: nameConflict ? relationNote.trim() : null,
      });
      setConfirmed(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book this test");
    } finally {
      setBooking(false);
    }
  };

  if (confirmed) {
    const reportUrl = `${window.location.origin}/care/report/${confirmed.id}?t=${confirmed.report_token}`;
    return (
      <div className="space-y-4" data-testid="pathology-confirmed">
        <Card className="border-emerald-200">
          <CardContent className="p-6 text-center space-y-3">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <div className="text-lg font-medium">Test booked</div>
            <p className="text-sm text-slate-600">
              {confirmed.collection_mode === "home"
                ? "A collector will visit you to draw the sample."
                : "Please walk in at your convenience for sample collection."}
            </p>
            <p className="mono text-sm text-slate-800">Total ₹{Number(confirmed.total).toLocaleString("en-IN")}</p>
            {confirmed.relation_note && (
              <p className="text-xs text-slate-400">
                {confirmed.patient_name} ({confirmed.relation_note})
              </p>
            )}
          </CardContent>
        </Card>
        <SaveLinkBanner
          url={reportUrl}
          primary={primary}
          message="Here's my lab test booking — I'll use this link to check my report:"
        />
      </div>
    );
  }

  if (loadingTests) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (tests.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-8">No tests are available for booking yet.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Select tests</Label>
        <div className="border border-slate-200 rounded-lg divide-y">
          {tests.map((t) => (
            <label key={t.id} className="flex items-center justify-between gap-3 p-3 cursor-pointer">
              <div className="flex items-center gap-2.5">
                <Checkbox
                  checked={selected.has(t.id)}
                  onCheckedChange={() => toggle(t.id)}
                  data-testid={`care-test-${t.id}`}
                />
                <div>
                  <div className="text-sm">{t.name}</div>
                  {t.sample_type && <div className="text-xs text-slate-400">{t.sample_type}</div>}
                </div>
              </div>
              <span className="mono text-sm text-slate-600">₹{Number(t.price).toLocaleString("en-IN")}</span>
            </label>
          ))}
        </div>
        {selected.size > 0 && (
          <div className="flex justify-between text-sm font-medium px-1">
            <span>Total</span>
            <span className="mono">₹{total.toLocaleString("en-IN")}</span>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>Sample collection</Label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setMode("walk_in")}
            className={`flex items-center justify-center gap-2 rounded-lg border p-3 text-sm ${
              mode === "walk_in" ? "border-2" : "border-slate-200"
            }`}
            style={mode === "walk_in" ? { borderColor: primary, background: `${primary}0C` } : undefined}
          >
            <Building2 className="w-4 h-4" /> Walk-in
          </button>
          <button
            type="button"
            onClick={() => setMode("home")}
            className={`flex items-center justify-center gap-2 rounded-lg border p-3 text-sm ${
              mode === "home" ? "border-2" : "border-slate-200"
            }`}
            style={mode === "home" ? { borderColor: primary, background: `${primary}0C` } : undefined}
          >
            <HomeIcon className="w-4 h-4" /> Home visit
          </button>
        </div>
      </div>

      {mode === "home" && (
        <div className="space-y-2">
          <Label htmlFor="care-address">Collection address</Label>
          <Input id="care-address" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="care-address-input" />
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="care-p-name">Your name</Label>
          <Input id="care-p-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="care-path-name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="care-p-phone">Phone number</Label>
          <Input
            id="care-p-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onBlur={checkPhone}
            data-testid="care-path-phone"
          />
        </div>
      </div>
      {nameConflict && (
        <div className="space-y-2">
          <Label htmlFor="care-path-relation">
            This phone is on file for <b>{existingName}</b> — how is {name.trim() || "this patient"} related?
          </Label>
          <Input
            id="care-path-relation"
            placeholder={`e.g. wife of ${existingName}`}
            value={relationNote}
            onChange={(e) => setRelationNote(e.target.value)}
            data-testid="care-path-relation"
          />
        </div>
      )}
      <ConsentLine checked={consent} onChange={setConsent} testId="care-path-consent" />
      <Button
        onClick={submit}
        disabled={booking}
        className="w-full"
        style={{ background: primary }}
        data-testid="care-path-submit"
      >
        {booking ? "Booking…" : `Confirm booking${total > 0 ? ` · ₹${total.toLocaleString("en-IN")}` : ""}`}
      </Button>
    </div>
  );
}

export default function CareBooking() {
  const { ownerId, shopName, slug, brand, status, hasCatalog } = useTenant();
  const [tab, setTab] = useState("doctor");
  const primary = brand?.primary_color || "#0f6e64";

  if (status === "loading" || status === "idle") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (status === "notfound" || !ownerId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-2">
            <XCircle className="w-8 h-8 text-red-500 mx-auto" />
            <div className="text-lg font-medium">This booking page doesn't exist.</div>
            <p className="text-sm text-slate-500">Double-check the link you were given.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          {brand?.logo_url ? (
            <img src={brand.logo_url} alt={shopName} className="w-9 h-9 rounded object-cover" />
          ) : (
            <div className="w-9 h-9 rounded flex items-center justify-center" style={{ background: primary }}>
              <Stethoscope className="w-4 h-4 text-white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{shopName || "Care booking"}</div>
            <div className="mono text-[10px] uppercase tracking-widest text-slate-400">
              Doctor visits &amp; lab tests
            </div>
          </div>
          {hasCatalog && slug && (
            <Link
              to={`/shop/${slug}`}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md text-white hover:opacity-90 shrink-0"
              style={{ background: primary }}
              data-testid="care-browse-products"
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">Browse products</span>
              <span className="sm:hidden">Shop</span>
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-lg">
          <button
            type="button"
            onClick={() => setTab("doctor")}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition ${
              tab === "doctor" ? "bg-white shadow-sm" : "text-slate-500"
            }`}
            data-testid="care-tab-doctor"
          >
            <Stethoscope className="w-4 h-4" /> Book a doctor visit
          </button>
          <button
            type="button"
            onClick={() => setTab("pathology")}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition ${
              tab === "pathology" ? "bg-white shadow-sm" : "text-slate-500"
            }`}
            data-testid="care-tab-pathology"
          >
            <TestTube2 className="w-4 h-4" /> Book a lab test
          </button>
        </div>

        <Card>
          <CardContent className="p-4">
            {tab === "doctor" ? (
              <DoctorBooking ownerId={ownerId} primary={primary} />
            ) : (
              <PathologyBooking ownerId={ownerId} primary={primary} />
            )}
          </CardContent>
        </Card>

        <p className="text-center text-[11px] text-slate-400 flex items-center justify-center gap-1">
          <Calendar className="w-3 h-3" /> Already booked a lab test? Use the link you were given to check your report.
        </p>
      </main>
    </div>
  );
}
